/**
 * padFocus — «одна клавиатура за раз» (06.09, владелец: «клик в поле ввода
 * вызывает кроме кастомной клавиатуры стандартную»). Хосты кастомных падов
 * глушат фокус через preventDefault на pointerdown, поэтому фокус (и
 * клавиатура ОС) оставался в предыдущем нативном поле — видны обе. Пад на
 * открытии уводит фокус из чужого поля; docked-TextPad, наоборот, держит фокус
 * в своём поле-хосте (inputMode="none" — клавиатура ОС не поднимается) и
 * ведёт каретку через selectionStart.
 */
export type PadHost = HTMLTextAreaElement | HTMLInputElement;

export function isTextField(el: Element | null | undefined): el is PadHost {
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA");
}

/** Снять фокус с активного нативного поля, если это не `keep` (клавиатура ОС уходит). */
export function blurOtherField(keep?: Element | null): void {
  const a = document.activeElement;
  if (!a || a === keep) return;
  if (isTextField(a) || (a as HTMLElement).isContentEditable) (a as HTMLElement).blur();
}

/** Каретка хоста: selectionStart, если поле в фокусе и оно число; иначе fallback. */
export function readHostCaret(host: PadHost | null | undefined, fallback: number, len: number): number {
  if (!host || document.activeElement !== host) return Math.min(fallback, len);
  const s = host.selectionStart;
  return typeof s === "number" ? Math.max(0, Math.min(s, len)) : Math.min(fallback, len);
}

/** Конец выделения хоста (≥ caret), если поле в фокусе; без выделения — caret (18.09, Даша). */
export function readHostSelEnd(host: PadHost | null | undefined, caret: number, len: number): number {
  if (!host || document.activeElement !== host) return caret;
  const e = host.selectionEnd;
  return typeof e === "number" ? Math.max(caret, Math.min(e, len)) : caret;
}

/** Поставить каретку хоста (без выделения). Значение контролируемое — в value не пишем. */
export function setHostCaret(host: PadHost | null | undefined, caret: number): void {
  if (!host || !host.isConnected) return;
  try {
    const c = Math.max(0, Math.min(caret, host.value.length));
    if (host.selectionStart !== c || host.selectionEnd !== c) host.setSelectionRange(c, c);
  } catch { /* input type без выделения (number/email) — каретку не ведём */ }
}

/** Гарантировать фокус на хосте docked-пада; вернуть, был ли он уже в фокусе. */
export function focusHost(host: PadHost | null | undefined): boolean {
  if (!host || !host.isConnected) return false;
  if (document.activeElement === host) return true;
  try { host.focus({ preventScroll: true }); } catch { host.focus(); }
  return false;
}
